import axios from "axios";
import { memoizer } from "@/utils/memoizer";

function calculateAge(birthdate) {
    // Obtener la fecha de hoy estrictamente en la zona horaria de Ciudad Juárez
    const formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Ciudad_Juarez',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });
    const juarezDate = formatter.format(new Date()); // Resultado: "YYYY-MM-DD"
    const [tYear, tMonth, tDay] = juarezDate.split("-");
    const today = new Date(tYear, tMonth - 1, tDay);

    // Evitar desfase de zona horaria al parsear la fecha de nacimiento "YYYY-MM-DD"
    const [bYear, bMonth, bDay] = birthdate.split("T")[0].split("-");
    const birth = new Date(bYear, bMonth - 1, bDay);
    
    let years = today.getFullYear() - birth.getFullYear();
    let months = today.getMonth() - birth.getMonth();
    let days = today.getDate() - birth.getDate();
    
    if (days < 0) {
        months--;
        const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        days += prevMonth.getDate();
    }
    
    if (months < 0) {
        years--;
        months += 12;
    }
    
    const totalMonths = (years * 12) + months;
    
    if (totalMonths < 1) {
        return `${days} día${days !== 1 ? 's' : ''}`;
    } else if (totalMonths < 24) {
        return `${totalMonths} mes${totalMonths !== 1 ? 'es' : ''}`;
    } else {
        return `${years} año${years !== 1 ? 's' : ''}`;
    }
}

function parseBirthDate(birthdate) {
    const [year, month, day] = birthdate.split("-");
    return `${day}/${month}/${year}`;
}

function ucwords(input) {
    input = String(input || "");
    return input
        .replace(/\s+/g, " ")
        .trim()
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
}

function formatted(patientsArray) {
    if (patientsArray) {
        const formatted = patientsArray.map((patient) => ({
            id:
                typeof patient.id !== "undefined"
                    ? patient.id.split("/")[1]
                    : patient._key,
            nombre: ucwords(patient.name),
            primerApellido: ucwords(patient.lastName[0]),
            segundoApellido: ucwords(patient.lastName[1]),
            birthdate: parseBirthDate(patient.birthDate),
            edad: calculateAge(patient.birthDate),
            sexo: patient.gender,
            telecom: patient.telecom
                ? patient.telecom.map((contact) => ({
                    type: contact.system,
                    value: contact.value,
                }))
                : [],
            peso: (patient.patient_weight !== null && patient.patient_weight !== undefined && patient.patient_weight !== '') ? Number(patient.patient_weight) : null,
            estatura: (patient.patient_height !== null && patient.patient_height !== undefined && patient.patient_height !== '') ? Number(patient.patient_height) : null,
            card: patient.loyalty_card ? patient.loyalty_card : null,
            alert_notifications: patient.alert_notifications,
            occupation: patient.occupation,
        }));
        return formatted;
    }
}

export function getAllPatients(endpont = "") {
    const url = `api/patient${endpont}`;
    return new Promise((resolve, reject) => {
        axios
            .get(url)
            .then((response) => {
                const patient = response.data.data;
                const formattedPatients = formatted(patient);
                resolve(formattedPatients);
            })
            .catch((error) => {
                reject(error);
            });
    });
}

export function getPatientsByPractitioner(idPractitioner) {
    return new Promise((resolve, reject) => {
        axios
            .get(`/api/practitioner/${idPractitioner}/patients`)
            .then((response) => {
                resolve(response.data.data);
            })
            .catch((error) => reject(error));
    });
}

export function getPatientById(idPatient) {
    const url = `/api/patient/${idPatient}`;
    return new Promise((resolve, reject) => {
        axios
            .get(url)
            .then((response) => {
                const patient = response.data.data;
                const formattedPatient = formatted(patient);
                resolve(formattedPatient[0]);
            })
            .catch((error) => {
                console.error("No se encontro la data del paciente");
                reject(error);
            });
    });
}

export function getPatientByNameAndBirthdate(data) {
    return new Promise((resolve, reject) => {
        axios
            .post(`/api/patient/find`, data)
            .then((response) => {
                const patient = response.data.data;
                const formattedPatient = formatted(patient);
                resolve(formattedPatient);
            })
            .catch((error) => {
                console.error("No se encontro la data del paciente");
                reject(error);
            });
    });
}

export function putPacientes(
    idPatient,
    data,
    config = {},
    bypassValidation = false
) {
    return new Promise((resolve, reject) => {
        axios
            .put(`/api/patient/${idPatient}?bypass=${bypassValidation}`, data, config)
            .then((response) => {
                resolve(response);
            })
            .catch((error) => {
                reject(error);
            });
    });
}

export async function postPaciente(data, bypassValidation = false) {
    return new Promise((resolve, reject) => {
        axios
            .post(`/api/patient?bypass=${bypassValidation}`, data)
            .then((response) => {
                const patient = response.data.data;
                resolve(patient);
            })
            .catch((error) => {
                console.error("Error en la solicitud:", error);
                reject(error);
            });
    });
}

export function getPatientByRecentConsultations(idPractitioner) {
    const url = `/api/patient/consultations/${idPractitioner}`;
    return new Promise((resolve, reject) => {
        axios
            .get(url)
            .then((response) => {
                const patient = response.data.data;
                const formattedPatients = formatted(patient);
                resolve(formattedPatients);
            })
            .catch((error) => {
                console.error("No se encontro la data del paciente");
                reject(error);
            });
    });
}

export const memoizedGetPatientById = memoizer(getPatientById);
